'use client';

/* eslint-disable react-hooks/refs -- deliberate "ref-guarded render-time setState" pattern (see smokeball-sync-fields.tsx); permitted by React but not understood by react-hooks/refs. */

import { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  getActionStepMatterTypes,
  type MatterType
} from '@/lib/services/integrations/actionstep';
import { AUSTRALIAN_STATES } from '@/lib/constants';
import { useLawFirmProfile } from '@/lib/services/account';

export interface ActionStepSyncConfig {
  stateOfMatter: string;
  matterTypeId: string;
}

export interface ActionStepSyncFieldsProps {
  initialConfig?: {
    stateOfMatter?: string;
    matterTypeId?: string;
  };
  onConfigChange: (config: ActionStepSyncConfig | null) => void;
  /**
   * Reports each field's live value independently of the other, on every
   * change — unlike `onConfigChange`, which only emits once both fields are
   * set (it gates Save-button readiness). Consumers driving a per-field
   * requirements checklist should read from this, not from `onConfigChange`.
   */
  onFieldsChange?: (fields: ActionStepSyncConfig) => void;
  onIntegrationStatus?: (enabled: boolean) => void;
  /** Override default state (e.g. from decedent address) */
  defaultState?: string;
  /** Styling variant */
  variant?: 'detail' | 'create';
}

/* -------------------------------------------------------------------------- */
/* Integration status + catalogue — ActionStep's catalogue is a flat, small,   */
/* per-firm list (GET actiontypes), fetched once with no location/search axis  */
/* (unlike Smokeball's jurisdiction-filtered, dynamically-searched catalogue). */
/* -------------------------------------------------------------------------- */

type IntegrationStatus = 'loading' | 'enabled' | 'disabled' | 'error';

interface IntegrationState {
  status: IntegrationStatus;
  matterTypes: MatterType[];
}

function useActionStepIntegration(): IntegrationState {
  const [state, setState] = useState<IntegrationState>({
    status: 'loading',
    matterTypes: []
  });

  useEffect(() => {
    let cancelled = false;

    async function run() {
      const result = await getActionStepMatterTypes();
      if (cancelled) return;
      if (result.success && result.data) {
        setState({ status: 'enabled', matterTypes: result.data.matterTypes });
      } else if (result.statusCode === 409) {
        setState({ status: 'disabled', matterTypes: [] });
      } else {
        setState({ status: 'error', matterTypes: [] });
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}

/* -------------------------------------------------------------------------- */
/* Form reducer                                                                */
/* -------------------------------------------------------------------------- */

interface FormState {
  /** `null` = user has not touched the field; derive from default in render. */
  userSelectedStateOfMatter: string | null;
  matterTypeId: string;
}

type FormAction =
  | { type: 'SET_STATE_OF_MATTER'; value: string }
  | { type: 'SET_MATTER_TYPE'; id: string };

function formReducer(state: FormState, action: FormAction): FormState {
  switch (action.type) {
    case 'SET_STATE_OF_MATTER':
      return { ...state, userSelectedStateOfMatter: action.value };
    case 'SET_MATTER_TYPE':
      return { ...state, matterTypeId: action.id };
  }
}

function createInitialFormState(
  initialConfig: ActionStepSyncFieldsProps['initialConfig']
): FormState {
  return {
    userSelectedStateOfMatter: initialConfig?.stateOfMatter ?? null,
    matterTypeId: initialConfig?.matterTypeId ?? ''
  };
}

/* -------------------------------------------------------------------------- */
/* Component                                                                   */
/* -------------------------------------------------------------------------- */

export function ActionStepSyncFields({
  initialConfig,
  onConfigChange,
  onFieldsChange,
  onIntegrationStatus,
  defaultState,
  variant = 'detail'
}: ActionStepSyncFieldsProps) {
  const { profile: lawFirmProfile } = useLawFirmProfile();
  const integration = useActionStepIntegration();

  const [form, dispatchForm] = useReducer(
    formReducer,
    initialConfig,
    createInitialFormState
  );

  // `lawFirmProfile?.businessAddress?.state` may arrive after first render, so
  // we read it on every render and fall back to it when the user has not
  // explicitly chosen a value yet.
  const effectiveDefaultState =
    defaultState || lawFirmProfile?.businessAddress?.state || '';
  const selectedStateOfMatter =
    form.userSelectedStateOfMatter ?? effectiveDefaultState;

  // ActionStep's action types are jurisdiction-agnostic (no location axis to filter by),
  // and every type in the firm's catalogue is offered.
  //
  // ⚠️ Do NOT filter on `creatable` (LF-6378). This picker used to drop `canCreate='F'`
  // types on the basis that actioncreate refuses them. It doesn't: four such types were
  // each created against successfully on the live sandbox. Worse, a firm's own
  // purpose-made action type — the one a probate export actually wants — comes back
  // `canCreate='F'` with no way to change it in ActionStep, so the filter hid exactly
  // the type the user came here to choose.
  //
  // Sorted alphabetically by name — the PMS returns its own list order (unpredictable
  // and can drift under the firm), and the picker's order must not depend on it. Mirrors
  // the backend provider's searchMatterTypes, which already sorts the same way.
  const selectableMatterTypes = useMemo(
    () =>
      [...integration.matterTypes].sort((a, b) => a.name.localeCompare(b.name)),
    [integration.matterTypes]
  );

  // Stable refs so the emit effect's dep list stays narrow and doesn't re-run
  // every time the parent reassigns its handlers.
  const onConfigChangeRef = useRef(onConfigChange);
  const onFieldsChangeRef = useRef(onFieldsChange);
  const onIntegrationStatusRef = useRef(onIntegrationStatus);
  onConfigChangeRef.current = onConfigChange;
  onFieldsChangeRef.current = onFieldsChange;
  onIntegrationStatusRef.current = onIntegrationStatus;

  useEffect(() => {
    if (integration.status === 'loading') return;

    const integrationEnabled = integration.status === 'enabled';
    onIntegrationStatusRef.current?.(integrationEnabled);

    if (!integrationEnabled) {
      onConfigChangeRef.current(null);
      onFieldsChangeRef.current?.({ stateOfMatter: '', matterTypeId: '' });
      return;
    }

    // Report each field's live value independently — a requirements checklist
    // must reflect a single satisfied field even while the other is still
    // outstanding (LF-6368), unlike the completeness-gated emit below.
    onFieldsChangeRef.current?.({
      stateOfMatter: selectedStateOfMatter,
      matterTypeId: form.matterTypeId
    });

    if (form.matterTypeId && selectedStateOfMatter) {
      onConfigChangeRef.current({
        stateOfMatter: selectedStateOfMatter,
        matterTypeId: form.matterTypeId
      });
    } else {
      onConfigChangeRef.current(null);
    }
  }, [integration.status, selectedStateOfMatter, form.matterTypeId]);

  if (integration.status === 'loading') return null;
  if (integration.status === 'disabled') return null;
  if (integration.status === 'error') {
    return (
      <p className="text-destructive mt-6 w-full text-center text-xs">
        Error verifying integration status
      </p>
    );
  }

  const isCreate = variant === 'create';
  const fieldHeight = isCreate ? 'h-8' : 'h-9';
  const fieldBg = isCreate ? 'border-0 bg-[var(--input-bg)]' : '';

  return (
    <div className={cn('grid grid-cols-2', isCreate ? 'gap-3' : 'gap-4')}>
      {/* State of Matter */}
      <div className="space-y-1.5">
        <label className="text-foreground text-xs font-medium">
          State of Matter
        </label>
        <Select
          value={selectedStateOfMatter}
          onValueChange={(value) =>
            dispatchForm({ type: 'SET_STATE_OF_MATTER', value })
          }
        >
          <SelectTrigger className={cn(fieldHeight, 'text-sm', fieldBg)}>
            <SelectValue placeholder="Select state" />
          </SelectTrigger>
          <SelectContent>
            {AUSTRALIAN_STATES.map((state) => (
              <SelectItem key={state.value} value={state.value}>
                {state.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Action Type */}
      <div className="space-y-1.5">
        <label className="text-foreground text-xs font-medium">
          ActionStep Action Type
        </label>
        <Select
          value={form.matterTypeId}
          onValueChange={(id) => dispatchForm({ type: 'SET_MATTER_TYPE', id })}
        >
          <SelectTrigger className={cn(fieldHeight, 'text-sm', fieldBg)}>
            <SelectValue placeholder="Select action type" />
          </SelectTrigger>
          <SelectContent>
            {selectableMatterTypes.map((type) => (
              <SelectItem key={type.id} value={type.id}>
                {type.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
